export default {
  brand: {
    name: "Basetao Sheet",
    tagline: "The Basetao Resource Hub",
  },
  nav: {
    spreadsheet: "Spreadsheet",
    reviews: "Reviews",
    shipping: "Shipping",
    coupons: "Coupons",
    warehouse: "Warehouse",
    qc: "QC",
    guides: "Guides",
    safety: "Safety",
    cta: "Explore Spreadsheet",
    home: "Home",
    shoppingGuide: "Shopping Guide",
  },
  lang: {
    label: "Language",
    en: "English",
    fr: "Français",
    de: "Deutsch",
    es: "Español",
    pt: "Português",
  },
  categories: {
    title: "Shop by Category",
    dropdownLabel: "Browse Categories",
    browseCta: "Browse All Categories",
    shoes: "Shoes",
    hoodies: "Hoodies / Sweaters",
    tshirts: "T-Shirts",
    jackets: "Jackets",
    pants: "Pants / Shorts",
    headwear: "Headwear",
    sets: "Sets",
    underwear: "Underwear / Underpants",
    jersey: "Jersey",
    accessories: "Accessories",
    bags: "Bags",
    belts: "Belts",
    perfumes: "Perfumes",
    other: "Other Stuff",
  },
  hero: {
    eyebrow: "THE BASETAO RESOURCE HUB",
    title: "Basetao Spreadsheet — Find Products, Sellers, QC & Shipping Resources Faster",
    subtitle:
      "Explore the Basetao Spreadsheet, organized product resources, seller guides, QC information, shipping guides, warehouse tips, reviews and practical buying resources — all in one place.",
    primaryCta: "Basetao Spreadsheet",
    secondaryCta: "Buy It Now",
    trust: "Independent Resource • Updated Regularly • Beginner Friendly",
  },
  what: {
    title: "What Is a Basetao Spreadsheet?",
    subtitle:
      "A Basetao Spreadsheet is a structured way to organize and discover product information, sellers, links and buying resources. It can make product research faster and easier to navigate.",
    card1Title: "Find Products",
    card1Desc: "Discover organized product resources.",
    card2Title: "Research Sellers",
    card2Desc: "Review seller information at a glance.",
    card3Title: "Check QC",
    card3Desc: "Understand quality-control details.",
    card4Title: "Compare Shipping",
    card4Desc: "Understand shipping variables clearly.",
    cta: "Explore the Basetao Spreadsheet",
  },
  popular: {
    title: "Popular Searches",
    subtitle: "The topics shoppers look up most when researching Basetao.",
    items: [
      "Basetao Spreadsheet",
      "Basetao Review",
      "Basetao Shipping Cost",
      "Basetao Coupon",
      "Basetao Discount Code",
      "Basetao Warehouse",
      "Is Basetao Legit?",
      "Basetao QC",
    ],
  },
  why: {
    title: "Why Use This Site?",
    subtitle: "Built to make your Basetao research clearer and faster.",
    items: [
      { title: "Organized", desc: "Everything structured in one clear place." },
      { title: "Practical", desc: "Focused on real buying decisions." },
      { title: "Transparent", desc: "We explain limits and uncertainty openly." },
      { title: "Multilingual", desc: "Available in five languages." },
    ],
  },
  how: {
    title: "How It Works",
    subtitle: "A simple research flow from discovery to checkout.",
    steps: [
      { title: "Find", desc: "Browse organized product and seller resources." },
      { title: "Research", desc: "Review listings, sellers and links." },
      { title: "Compare", desc: "Weigh QC, shipping and price." },
      { title: "Verify", desc: "Check safety notes and red flags." },
      { title: "Continue", desc: "Head to the main website to buy." },
    ],
  },
  safety: {
    title: "Important Safety Note",
    text:
      "Product listings, sellers, shipping costs and promotions can change. Guides and spreadsheets organize information but cannot guarantee product quality, seller reliability or delivery results.",
    links: [
      "Seller Safety",
      "QC Guide",
      "Shipping Risk",
      "Payment Safety",
      "Is Basetao Legit?",
    ],
  },
  faq: {
    title: "Frequently Asked Questions",
    items: [
      {
        q: "What is a Basetao Spreadsheet?",
        a: "A Basetao Spreadsheet is a structured document that organizes products, sellers, prices, QC photos and shipping details in one place, making research easier to navigate.",
      },
      {
        q: "How does a Basetao Spreadsheet work?",
        a: "It groups useful information into columns and categories so you can find, compare and track items faster than scrolling through scattered chat messages.",
      },
      {
        q: "Is Basetao legit?",
        a: "Basetao is a long-running shopping agent used by many international buyers. This site is independent, so always do your own verification of sellers and orders.",
      },
      {
        q: "How much does Basetao shipping cost?",
        a: "Shipping cost depends on weight, dimensions, destination, method and insurance. Check the shipping guide on the main website for current rates.",
      },
      {
        q: "Where can I find Basetao coupons?",
        a: "Promotions change often. Look for verified, current offers rather than relying on expired codes you may find elsewhere.",
      },
    ],
  },
  finalCta: {
    title: "Ready to Explore the Basetao Spreadsheet?",
    subtitle: "Browse organized products, sellers and resources on the main website.",
    button: "Explore Basetao Spreadsheet",
  },
  guides: {
    title: "Basetao Guides & Resources",
    subtitle:
      "Pillar articles that cover everything from the spreadsheet and reviews to shipping, coupons, the warehouse and safety.",
    readMore: "Read Article",
  },
  footer: {
    description:
      "An independent informational resource about the Basetao Spreadsheet. Not affiliated with or endorsed by Basetao.",
    resources: "Resources",
    about: "About",
    contact: "Contact",
    privacy: "Privacy",
    terms: "Terms",
    disclaimer: "Disclaimer",
    cookie: "Cookie Policy",
    languages: "Languages",
    copyright: "© 2026 Basetao Sheet. All rights reserved.",
  },
  article: {
    breadcrumb: "Home › Guides",
    h1: "Basetao Spreadsheet: The Complete Guide to Products, Sellers, QC & Shipping",
    updated: "Last updated: August 19, 2026",
    quickAnswerLabel: "Quick Answer",
    quickAnswer:
      "A Basetao Spreadsheet is a shared, structured document that organizes products, sellers, prices, QC photos and shipping details in one place. It helps you find items faster, compare sellers and track orders — far easier than scrolling through scattered chat messages.",
    tocTitle: "Table of Contents",
    sections: [
      {
        title: "What Is a Basetao Spreadsheet?",
        body: [
          "A Basetao Spreadsheet is essentially a shared table that brings together everything a shopper needs to research a purchase. Instead of hunting through chat history, seller albums or browser bookmarks, you get product names, images, seller details, prices, QC status and shipping notes laid out in clear columns.",
          "This kind of organization is especially useful when you are comparing many items at once. The spreadsheet acts as a single source of truth that you can scan quickly, which saves time and reduces the chance of mixing up similar products from different sellers.",
        ],
      },
      {
        title: "Why Shoppers Use a Basetao Spreadsheet",
        body: [
          "The main reason is speed. When research is structured, you spend less time searching and more time making decisions. A spreadsheet also keeps a paper trail of what you found, what you ordered and what you still need to check.",
          "For larger hauls or repeat purchases, a spreadsheet helps you stay consistent. You can track sellers you trust, note which items passed quality control and remember shipping choices that worked well before.",
        ],
      },
      {
        title: "How a Basetao Spreadsheet Is Organized",
        body: [
          "A typical spreadsheet groups information into a few core columns: product name, image, category, seller, price, QC status and a link to the item. Some also include shipping weight, warehouse notes and personal remarks.",
          "Categories usually mirror the store structure — shoes, hoodies, t-shirts, jackets, pants, headwear, sets, underwear, jerseys, accessories, bags, belts, perfumes and other items. This makes it easy to jump straight to the section you care about.",
        ],
      },
      {
        title: "How to Find Products With a Basetao Spreadsheet",
        body: [
          "Start with the category you are interested in, then scan the product names and images for items that match what you are looking for. Use the seller and price columns to narrow down your options.",
          "Once you spot a promising item, open its link on the main website to see full details, availability and any current offers. The spreadsheet is a starting point — the listing itself is where you confirm specifics.",
        ],
      },
      {
        title: "How to Research Sellers",
        body: [
          "Seller research matters because quality and reliability vary. Look at whether a seller appears consistently, how their products are described and whether past QC notes flag any recurring problems.",
          "Treat seller information as a starting signal rather than a guarantee. Conditions change, so re-check a seller each time you order, especially for higher-value items.",
        ],
      },
      {
        title: "Understanding QC Photos",
        body: [
          "QC (quality control) photos are one of the most valuable parts of the buying process. They let you see the actual item before it ships, so you can catch sizing, color or defect issues early.",
          "When reviewing QC photos, compare them against the original listing. Look at details like stitching, logos, colors and measurements. If something looks off, ask questions before the item is shipped.",
        ],
      },
      {
        title: "Shipping Costs and Options",
        body: [
          "Shipping cost is not a fixed number — it depends on parcel weight, dimensions, destination country, the shipping method you choose and whether you add insurance. Heavier or bulkier parcels generally cost more.",
          "Different shipping lines balance speed against price. Some are faster but pricier, while others are cheaper but slower. The right choice depends on how quickly you need the parcel and how much you want to spend.",
        ],
      },
      {
        title: "Common Mistakes to Avoid",
        body: [
          "A frequent mistake is ordering before checking QC photos, which makes it harder to fix problems. Another is ignoring weight when planning shipping, which can lead to an unexpectedly high bill.",
          "It is also easy to mix up similar items from different sellers. Using the spreadsheet to record which item came from which seller helps you avoid confusion and keeps your orders organized.",
        ],
      },
      {
        title: "Safety and Red Flags",
        body: [
          "Keep a healthy level of caution. Be wary of sellers with little history, prices that seem too good to be true, or listings that reuse unrelated photos. Small deposits or unusual payment requests can also be warning signs.",
          "This website is an independent resource, so always verify information yourself. No guide can guarantee product quality, seller reliability or delivery results — use the resources here as a helpful starting point, not a replacement for your own judgment.",
        ],
      },
    ],
    tableTitle: "Spreadsheet vs. Scattered Notes",
    tableHeaders: ["Feature", "Basetao Spreadsheet", "Manual Chat Notes"],
    tableRows: [
      ["Product discovery", "Organized and filterable", "Hard to search"],
      ["Seller research", "Structured columns", "Scattered messages"],
      ["QC tracking", "Dedicated QC fields", "Mixed with chat"],
      ["Shipping planning", "Clear at a glance", "Requires digging"],
      ["Best for", "Serious researchers", "Occasional browsers"],
    ],
    faqTitle: "Frequently Asked Questions",
    faqs: [
      {
        q: "Is a Basetao Spreadsheet free to use?",
        a: "Spreadsheets are typically shared resources. Check the main website for the current version and any access details.",
      },
      {
        q: "How often is the spreadsheet updated?",
        a: "Updates vary. Product listings, sellers and prices change, so treat the information as a snapshot and confirm details on the main website before ordering.",
      },
      {
        q: "Can I use the spreadsheet on my phone?",
        a: "Most structured documents work on both desktop and mobile. The layout may feel more comfortable on a larger screen when you are comparing many columns.",
      },
      {
        q: "Does the spreadsheet guarantee product quality?",
        a: "No. It organizes information but cannot guarantee quality, seller reliability or delivery results. Always review QC photos and do your own checks.",
      },
    ],
    conclusion:
      "A Basetao Spreadsheet is a practical tool for anyone who wants to research products, sellers, QC and shipping more efficiently. It brings scattered information into one clear view, so you can make better buying decisions and spend less time searching. Remember that details change, so use the spreadsheet as a guide and always confirm the latest information on the main website before you buy.",
    relatedTitle: "Related Resources",
    backHome: "Back to Home",
    ctaLabel: "Explore the Basetao Spreadsheet",
    conclusionTitle: "Conclusion",
    readGuide: "Read the Guide",
    toc: [
      "What Is a Basetao Spreadsheet?",
      "Why Shoppers Use a Basetao Spreadsheet",
      "How a Basetao Spreadsheet Is Organized",
      "How to Find Products",
      "How to Research Sellers",
      "Understanding QC Photos",
      "Shipping Costs and Options",
      "Common Mistakes to Avoid",
      "Safety and Red Flags",
    ],
  },
};